import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Trash2, AlertTriangle, Check, Plus } from 'lucide-react';
// @ts-ignore - Module Federation dynamic import
import { APIKeyService } from 'shell/APIKeyService';

interface APIKey {
  id: string;
  companyId: string;
  name: string;
  keyPrefix: string;
  status: 'active' | 'revoked';
  createdAt: Date;
  lastUsedAt?: Date;
  permissions: string[];
}

interface CreateAPIKeyInput {
  name: string;
  permissions: string[];
  headerName?: string;
}

interface APIKeyResponse {
  id: string;
  key: string;
  name: string;
  keyPrefix: string;
  status: 'active' | 'revoked';
  createdAt: Date;
  permissions: string[];
}

interface ApiKeyManagementProps {
  companyId?: string | null;
}

export const ApiKeyManagement: React.FC<ApiKeyManagementProps> = ({ companyId }) => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    permissions: [] as string[],
    headerName: 'x-make-apikey',
  });

  // Use companyId directly from props - no fallback logic
  // Parent component is responsible for providing the correct company ID

  const loadAPIKeys = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const keys = await APIKeyService.getCompanyAPIKeys(companyId);
      setApiKeys(keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadAPIKeys();
    }
  }, [companyId, loadAPIKeys]);

  const handleCreateKey = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a name for the API key');
      return;
    }

    if (!companyId) return;

    try {
      const input: CreateAPIKeyInput = {
        name: formData.name,
        permissions: formData.permissions,
        headerName: formData.headerName,
      };

      const result: APIKeyResponse = await APIKeyService.generateAPIKey(companyId, input);
      setNewlyCreatedKey(result.key);
      setShowKeyDialog(true);
      setShowCreateDialog(false);

      // Reset form
      setFormData({
        name: '',
        permissions: ['read'],
        headerName: 'x-make-apikey',
      });

      await loadAPIKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (!window.confirm(`Revoke API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await APIKeyService.revokeAPIKey(keyId);
      await loadAPIKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
      alert('Failed to revoke API key');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(date));
  };

  const formatRelativeTime = (date?: Date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(date);
  };

  // If no company is selected, show a message
  if (!companyId) {
    return (
      <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
        <div className="text-4xl mb-4">🏢</div>
        <p>Please select a company</p>
        <p className="text-sm mt-2">
          Choose a company from the dropdown above to manage API keys
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Generate API keys for programmatic access to the podcast manager.
        </p>
        <Button onClick={() => setShowCreateDialog(true)} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Generate New Key
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading API keys...</p>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
          <div className="text-4xl mb-4">🔑</div>
          <p>No API keys generated yet</p>
          <p className="text-sm mt-2">
            Create your first API key to enable programmatic access
          </p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.map((key) => {
                // Default to 'active' for keys without status (legacy data)
                const status = key.status || 'active';
                const isActive = status !== 'revoked';

                return (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {key.keyPrefix}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {(key.permissions || []).map((p) => (
                          <Badge key={p} variant="secondary" className="text-xs">
                            {p}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(key.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(key.lastUsedAt)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={isActive ? 'default' : 'secondary'}
                        className="capitalize"
                      >
                        {status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeKey(key.id, key.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate New API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for programmatic access to podcast episodes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="keyName">Key Name</Label>
              <Input
                id="keyName"
                placeholder="e.g., Production Server, Development"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="space-y-2">
                {[
                  { id: 'read', label: 'Read episodes' },
                  { id: 'write', label: 'Create/update episodes' },
                  { id: 'delete', label: 'Delete episodes' },
                  { id: 'podcast:callback', label: 'Webhook callbacks' },
                  { id: 'mcp:read', label: 'MCP status access (Claude/ChatGPT)' },
                ].map((perm) => (
                  <div key={perm.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={perm.id}
                      checked={formData.permissions.includes(perm.id)}
                      onChange={() => handlePermissionToggle(perm.id)}
                      className="h-4 w-4"
                    />
                    <label htmlFor={perm.id} className="text-sm cursor-pointer">
                      {perm.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headerName">API Key Header Name</Label>
              <Input
                id="headerName"
                placeholder="x-make-apikey"
                value={formData.headerName}
                onChange={(e) => setFormData({ ...formData, headerName: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                HTTP header name for outbound webhooks (e.g., x-make-apikey for Make.com)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKey}>Generate Key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Generated Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              API Key Generated
            </DialogTitle>
            <DialogDescription>
              Save this key securely. It won't be shown again.
            </DialogDescription>
          </DialogHeader>

          <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> This is the only time you'll see this key.
              Store it in a secure location.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Your API Key</Label>
            <div className="flex gap-2">
              <Input
                value={newlyCreatedKey || ''}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => newlyCreatedKey && copyToClipboard(newlyCreatedKey)}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => {
              setShowKeyDialog(false);
              setNewlyCreatedKey(null);
              setCopied(false);
            }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
